// IpsSensors.tsx
import { useState, useCallback, useEffect } from "react";
import {
    ShieldAlert, Loader2, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown
} from "lucide-react";
import baseapi from "../../../../../services/baseapi";
import { showError } from "../../../../../utils/toast";


// ── Types ─────────────────────────────────────────────────────────────────────
interface IpsEntry {
    id: number;
    os: string;
    log: string;
    action: string;
    status: string;
    location: string;
    protocol: string;
    severity: string;
    "log-packet": string;
    quarantine: string;
    "quarantine-log": string;
    "quarantine-expiry": string;
    "rate-mode": string;
    "rate-count": number;
    "rate-track": string;
    "rate-duration": number;
    application: string;
    "log-attack-context": string;
    "default-action": string;
    "default-status": string;
}

interface IpsSensor {
    name: string;
    comment: string;
    entries: IpsEntry[];
    "extended-log": string;
    "block-malicious-url": string;
    "scan-botnet-connections": string;
}

// Flat row = sensor-level fields + one entry
interface FlatRow extends IpsEntry {
    _sensorName: string;
    _sensorComment: string;
    _blockMaliciousUrl: string;
    _scanBotnet: string;
    _extendedLog: string;
    _rowKey: string;
}

// ── Chips ─────────────────────────────────────────────────────────────────────
const ValChip = ({ val }: { val: string | number | undefined }) => {
    const str = String(val ?? "");
    if (!str || str === "none" || str === "") return <span className="text-gray-600 text-[9px]">—</span>;
    const lower = str.toLowerCase();
    const style =
        lower === "enable" || lower === "block"
            ? "bg-green-500/10 border-green-500/20 text-green-400"
            : lower === "disable"
                ? "bg-red-700/40 border-red-600/30 text-red-200"
                : lower === "default"
                    ? "bg-blue-500/10 border-blue-500/20 text-blue-400"
                    : lower === "pass"
                        ? "bg-cyan-500/10 border-cyan-500/20 text-cyan-400"
                        : lower === "none"
                            ? "bg-gray-700/30 border-gray-600/20 text-gray-600"
                            : lower.includes("critical")
                                ? "bg-red-500/10 border-red-500/20 text-red-400"
                                : lower.includes("high")
                                    ? "bg-orange-500/10 border-orange-500/20 text-orange-400"
                                    : lower.includes("medium")
                                        ? "bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
                                        : lower === "all"
                                            ? "bg-gray-600/20 border-gray-600/20 text-gray-200"
                                            : "bg-purple-500/10 border-purple-500/20 text-purple-400";

    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold border uppercase whitespace-nowrap ${style}`}>
            {str}
        </span>
    );
};

const SeverityChips = ({ val }: { val: string }) => {
    if (!val) return <ValChip val="—" />;
    if (val === "all") return <ValChip val="all" />;
    const parts = val.split(" ").filter(Boolean);
    return (
        <div className="flex flex-wrap gap-0.5">
            {parts.map(p => <ValChip key={p} val={p} />)}
        </div>
    );
};

// ── Column definitions ────────────────────────────────────────────────────────
type ColKey = keyof FlatRow;

const COLUMNS: { key: ColKey; label: string; width: string; render?: (row: FlatRow) => React.ReactNode }[] = [
    {
        key: "_sensorName",
        label: "Sensor Name",
        width: "min-w-[140px]",
        render: (row) => (
            <div>
                <p className="text-[11px] font-black text-white">{row._sensorName.replace(/_/g, " ")}</p>

            </div>
        ),
    },
    {
        key: "id", label: "Rule #", width: "w-14",
        render: row => <span className="text-[10px] font-mono text-gray-200">{row.id}</span>
    },
    {
        key: "severity", label: "Severity", width: "min-w-[140px]",
        render: row => <SeverityChips val={row.severity} />
    },
    {
        key: "action", label: "Action", width: "w-20",
        render: row => <ValChip val={row.action} />
    },
    {
        key: "status", label: "Status", width: "w-20",
        render: row => <ValChip val={row.status} />
    },
    {
        key: "location", label: "Location", width: "w-20",
        render: row => <ValChip val={row.location} />
    },
    {
        key: "protocol", label: "Protocol", width: "min-w-[100px]",
        render: row => <span className="text-[10px] font-mono text-gray-300">{row.protocol || "—"}</span>
    },
    {
        key: "os", label: "OS", width: "w-14",
        render: row => <ValChip val={row.os} />
    },
    {
        key: "application", label: "App", width: "w-14",
        render: row => <ValChip val={row.application} />
    },
    {
        key: "log", label: "Log", width: "w-14",
        render: row => <ValChip val={row.log} />
    },
    {
        key: "log-packet", label: "Log Pkt", width: "w-16",
        render: row => <ValChip val={row["log-packet"]} />
    },
    {
        key: "log-attack-context", label: "Atk Ctx", width: "w-16",
        render: row => <ValChip val={row["log-attack-context"]} />
    },
    {
        key: "quarantine", label: "Quarantine", width: "w-20",
        render: row => <ValChip val={row.quarantine} />
    },
    {
        key: "quarantine-log", label: "Quar Log", width: "w-16",
        render: row => <ValChip val={row["quarantine-log"]} />
    },
    {
        key: "quarantine-expiry", label: "Quar Expiry", width: "w-20",
        render: row => <span className="text-[10px] font-mono text-gray-200">{row["quarantine-expiry"] || "—"}</span>
    },
    {
        key: "rate-mode", label: "Rate Mode", width: "w-24",
        render: row => <ValChip val={row["rate-mode"]} />
    },
    {
        key: "rate-duration", label: "Rate Dur (s)", width: "w-24",
        render: row => <span className="text-[10px] font-mono text-gray-200">{row["rate-duration"]}</span>
    },
    {
        key: "rate-count", label: "Rate Cnt", width: "w-16",
        render: row => <span className="text-[10px] font-mono text-gray-200">{row["rate-count"]}</span>
    },
    {
        key: "rate-track", label: "Rate Track", width: "w-20",
        render: row => <ValChip val={row["rate-track"]} />
    },
    {
        key: "_blockMaliciousUrl", label: "Block Malicious URL", width: "w-24",
        render: row => <ValChip val={row._blockMaliciousUrl} />
    },
    {
        key: "_scanBotnet", label: "Scan Botnet", width: "w-24",
        render: row => <ValChip val={row._scanBotnet} />
    },
    {
        key: "_extendedLog", label: "Ext Log", width: "w-16",
        render: row => <ValChip val={row._extendedLog} />
    },
    {
        key: "default-action", label: "Def Action", width: "w-20",
        render: row => <ValChip val={row["default-action"]} />
    },
    {
        key: "default-status", label: "Def Status", width: "w-20",
        render: row => <ValChip val={row["default-status"]} />
    },
    {
        key: "_sensorComment",
        label: "Sensor Comment",
        width: "min-w-[180px]",
        render: (row) => (
            <div>
                <p className="text-[8px] text-white">{row._sensorComment}</p>
            </div>
        ),
    },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// ── Main Component ────────────────────────────────────────────────────────────
export default function IpsSensors({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [rows, setRows] = useState<FlatRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [actionFilter, setActionFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sort, setSort] = useState<{ key: ColKey; dir: "asc" | "desc" }>({
        key: "_sensorName", dir: "asc",
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "ips_sensors",
                    }),
                    limit: 1,
                },
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                const sensors: IpsSensor[] = parsed.items ?? [];

                // Flatten: one row per entry × sensor
                const flat: FlatRow[] = [];
                sensors.forEach(sensor => {
                    (sensor.entries ?? []).forEach(entry => {
                        flat.push({
                            ...entry,
                            _sensorName: sensor.name,
                            _sensorComment: sensor.comment,
                            _blockMaliciousUrl: sensor["block-malicious-url"],
                            _scanBotnet: sensor["scan-botnet-connections"],
                            _extendedLog: sensor["extended-log"],
                            _rowKey: `${sensor.name}-${entry.id}`,
                        });
                    });
                });
                setRows(flat);
            } else showError("Failed to load IPS sensors");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { setPage(1); }, [search, actionFilter]);

    // Unique actions for filter
    const actions = Array.from(new Set(rows.map(r => r.action).filter(Boolean))).sort();

    // Filter
    const filtered = rows.filter(r => {
        if (actionFilter !== "all" && r.action !== actionFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                r._sensorName.toLowerCase().includes(q) ||
                r._sensorComment?.toLowerCase().includes(q) ||
                r.severity?.toLowerCase().includes(q) ||
                r.protocol?.toLowerCase().includes(q) ||
                r.location?.toLowerCase().includes(q)
            );
        }
        return true;
    });

    // Sort
    const sorted = [...filtered].sort((a, b) => {
        const av = (a as any)[sort.key];
        const bv = (b as any)[sort.key];
        const cmp = typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av ?? "").localeCompare(String(bv ?? ""));
        return sort.dir === "asc" ? cmp : -cmp;
    });

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageItems = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
    const start = sorted.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const end = Math.min(safePage * pageSize, sorted.length);

    const handleSort = (key: ColKey) => {
        setSort(prev => prev.key === key
            ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
            : { key, dir: "asc" });
        setPage(1);
    };

    return (
        <div className="flex flex-col gap-3">

            {/* Header */}
            {/* <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <div className="bg-red-600/20 p-1.5 rounded-lg border border-red-500/30">
                        <ShieldAlert size={16} className="text-red-400" />
                    </div>
                    <h1 className="text-[14px] font-black text-white tracking-wide">IPS Sensors</h1>
                    {!loading && (
                        <>
                            <span className="text-[10px] font-bold text-gray-200 bg-gray-800 border border-gray-700 px-2 py-0.5 rounded">
                                {Array.from(new Set(rows.map(r => r._sensorName))).length} sensors
                            </span>
                            <span className="text-[10px] font-bold text-gray-200 bg-gray-800 border border-gray-700 px-2 py-0.5 rounded">
                                {rows.length} rules
                            </span>
                        </>
                    )}
                </div>
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-red-400"} />
                </button>
            </div> */}

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
                {/* Search */}
                <div className="relative group">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-200 group-focus-within:text-red-400 transition-colors" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search sensor, severity, protocol..."
                        className="w-60 bg-gray-900 border border-gray-700 rounded-lg pl-7 pr-3 py-1.5 text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-red-500/50 transition-all"
                    />
                </div>

                {/* Action filter pills */}
                <div className="flex items-center gap-1 bg-gray-800/60 border border-gray-700 rounded-lg p-0.5">
                    <button
                        onClick={() => setActionFilter("all")}
                        className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${actionFilter === "all" ? "bg-gray-600 text-white" : "text-gray-200 hover:text-gray-300"
                            }`}
                    >
                        All
                    </button>
                    {actions.map(a => (
                        <button
                            key={a}
                            onClick={() => setActionFilter(a)}
                            className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${actionFilter === a ? "bg-gray-700 text-white" : "text-gray-200 hover:text-gray-300"
                                }`}
                        >
                            {a}
                        </button>
                    ))}
                </div>

                {(search || actionFilter !== "all") && (
                    <button
                        onClick={() => { setSearch(""); setActionFilter("all"); }}
                        className="text-[9px] text-red-400 hover:text-red-300 font-bold uppercase tracking-widest"
                    >
                        Clear
                    </button>
                )}
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-20">
                    <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">Loading IPS sensors...</span>
                </div>
            ) : sorted.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                    <ShieldAlert size={22} className="text-gray-200" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No rules found</span>
                </div>
            ) : (
                <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-700/60">
                                    {COLUMNS.map(col => (
                                        <th
                                            key={col.key}
                                            onClick={() => handleSort(col.key)}
                                            className={`px-3 py-2 text-[9px] font-bold text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group select-none ${col.width}`}
                                        >
                                            <div className="flex items-center gap-1">
                                                {col.label}
                                                {sort.key === col.key
                                                    ? sort.dir === "asc"
                                                        ? <ArrowUp size={9} className="text-red-400" />
                                                        : <ArrowDown size={9} className="text-red-400" />
                                                    : <ArrowUp size={9} className="opacity-0 group-hover:opacity-30 text-gray-200 transition-opacity" />
                                                }
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/30">
                                {pageItems.map((row) => (
                                    <tr key={row._rowKey} className="hover:bg-gray-700/20 transition-colors">
                                        {COLUMNS.map(col => (
                                            <td key={col.key} className="px-3 py-2 align-middle">
                                                {col.render
                                                    ? col.render(row)
                                                    : <ValChip val={String((row as any)[col.key] ?? "")} />
                                                }
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="flex items-center justify-between px-3 py-2 border-t border-gray-700/60 bg-gray-800/40 flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-gray-600 uppercase tracking-widest">Rows</span>
                                <select
                                    value={pageSize}
                                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    className="bg-gray-900 border border-gray-700 rounded px-1.5 py-0.5 text-[10px] text-gray-300 focus:outline-none"
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <span className="text-[9px] text-gray-200">
                                <span className="text-gray-300">{start}</span>
                                {" – "}
                                <span className="text-gray-300">{end}</span>
                                <span className="mx-1 text-gray-600">/</span>
                                <span className="text-gray-300 font-bold">{sorted.length}</span>
                                {sorted.length !== rows.length && (
                                    <span className="text-gray-600 ml-1">(filtered from {rows.length})</span>
                                )}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button disabled={safePage === 1} onClick={() => setPage(1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronsLeft size={13} />
                            </button>
                            <button disabled={safePage === 1} onClick={() => setPage(p => p - 1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronLeft size={13} />
                            </button>
                            <span className="px-2 py-0.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded text-[10px] font-bold mx-1 min-w-[28px] text-center">
                                {safePage}
                            </span>
                            <span className="text-[9px] text-gray-600">/ {totalPages}</span>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronRight size={13} />
                            </button>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronsRight size={13} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
